import { useEffect, useSyncExternalStore } from 'react';
import type { AssistantContext, MailEvent } from '@momomail/shared';
import { AffordError, AffordKit, type ActEvent, type UiRequest } from './affordkit';
import { api, screenClient } from './bridge';
import { confirmDialog } from '../components/Overlay';
import { SETTINGS_TABS } from '../components/Settings';
import { t } from './i18n';
import { reducedMotion } from './motion';
import { parseRoute, routeOf, screenRoutes, type ScreenPlace } from './screenRoutes';

/**
 * 馍馍 working this window's screen (AffordKit), as it does the phone's (apps/mobile/lib/afford/agent.dart). Each chat message goes with
 * this window's id and its screen as a tree (assistantContext) — from the chat here, and from the desktop cat's, which asks this window
 * for it. The server's assistant then asks (ServerEvent assistant-ui, for this window's id): look at the screen, or run calls — press,
 * type, go to a page — and this answers with the results and the screen after (POST /api/assistant/ui/:id). Before each act a ring
 * shows where it is about to press or type, so the user sees it happen.
 */

// (made in bridge.ts: the event WebSocket goes under it too, so the server knows when this window is gone)
export { screenClient };

/** What the app does for the agent: where the user is, taking them elsewhere, and whether the mail list is loading. */
export interface ScreenApp {
  current(): ScreenPlace;
  /** Throws (with what is wrong, for the agent) when it can't go there. */
  navigate(place: ScreenPlace): Promise<void> | void;
  loading(): boolean;
}

let app: ScreenApp | null = null;
/** The app is mounted: its screen goes with chat messages, and is worked. */
let mounted = false;
let pending = 0;

/** Work whose result the agent should see (a message being fetched, a draft being made): it waits for it before it looks. */
export async function screenLoading<T>(work: Promise<T>): Promise<T> {
  pending++;
  try {
    return await work;
  } finally {
    // (a moment more: what it set renders, and the list it switched to starts loading, before the agent may look)
    setTimeout(() => pending--, 50);
  }
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * What the agent set off has loaded (the mail list, a message being fetched, a search typed in), after a moment for it to start; at
 * most 12 s (a search on the mail server can take that long: past it, the agent sees it still loading and can look again).
 */
async function settle(): Promise<void> {
  await delay(300);
  const until = Date.now() + 12_000;
  while ((pending > 0 || app?.loading()) && Date.now() < until) await delay(100);
}

/** Before each press / type: a ring where it is, for a moment. */
async function show({ el }: ActEvent): Promise<void> {
  (el as HTMLElement).scrollIntoView?.({ block: 'nearest' });
  const box = el.getBoundingClientRect();
  if (!box.width && !box.height) return;
  const ring = document.createElement('div');
  ring.className = 'agent-ring';
  ring.setAttribute('aria-hidden', 'true');
  Object.assign(ring.style, { left: `${box.left - 4}px`, top: `${box.top - 4}px`, width: `${box.width + 8}px`, height: `${box.height + 8}px` });
  document.body.append(ring);
  await delay(reducedMotion() ? 250 : 420);
  ring.remove();
}

export const kit = new AffordKit({
  // 馍馍's own chat, the cat and what shows it working are not part of the screen it works
  skip: '.assistant, .window-cat, .cat-bed, .screen-work',
  confirm: async ({ node, tool }) =>
    (await confirmDialog({ title: t('让馍馍「{name}」吗？', { name: node?.name ?? tool?.name ?? '' }), message: t('这一步没法轻易撤销，馍馍想先问问你。'), confirm: t('好的'), cancel: t('不要') })) === 'confirm',
  // (a press that opens a mail, a search, a mailbox: the screen it sees next has what they loaded)
  settle,
  onAct: show,
});

/** What goes with a chat message: this window, and its screen as a tree (nothing before the app is up). */
export function screenContext(): Pick<AssistantContext, 'client' | 'ui'> {
  return mounted ? { client: screenClient, ui: kit.context() } : {};
}

/** The app (App.tsx, each render: its state as of now) puts its screen in the agent's hands while it is mounted. */
export function useScreen(next: ScreenApp): void {
  app = next;
  useEffect(() => {
    mounted = true;
    // (opened in the middle of a turn: locked from the start)
    refreshBusy();
    kit.setRoutes({
      current: () => (app ? routeOf(app.current()) : '/mail/inbox'),
      routes: screenRoutes(SETTINGS_TABS),
      navigate: async (route) => {
        const place = parseRoute(route);
        if (!place || !app) throw new AffordError('not_found', `No page ${route}`);
        await app.navigate(place);
      },
      settle,
    });
    const off = api().onEvent(onEvent);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      off();
      document.removeEventListener('visibilitychange', onVisibility);
      setWorking(false);
      kit.setRoutes(null);
      mounted = false;
    };
  }, []);
}

/**
 * Whether 馍馍 is working on a turn (assistant events; this window's own message counts from the moment it is sent). Meanwhile
 * nothing here talks to it: the cat, the cat's bed and whatever opens the chat do nothing until it is done (stop is in the chat, and
 * on the pill at the top while it works the screen).
 */
let busy = false;

function setBusy(on: boolean): void {
  if (on === busy) return;
  busy = on;
  recheck();
  for (const l of workingListeners) l();
}

/** The tool 馍馍 is on now (null: thinking, or not busy). */
let doing: string | null = null;

function setDoing(tool: string | null): void {
  if (tool === doing) return;
  doing = tool;
  for (const l of workingListeners) l();
}

/** What 馍馍 is on now (see doing), for the pill at the top. */
export function useAssistantDoing(): string | null {
  return useSyncExternalStore(
    (l) => (workingListeners.add(l), () => void workingListeners.delete(l)),
    () => doing,
  );
}

/** This window just sent 馍馍 a message: busy now, not when the server's event comes (a press on the cat meanwhile would open it). */
export function noteSending(): void {
  setBusy(true);
}

/** Back from sending (or it failed): what the server says the turn is. */
export function refreshBusy(): void {
  void api()
    .assistant.state()
    .then((s) => setBusy(s.busy))
    .catch(() => {});
}

/** Whether nothing may talk to 馍馍 now: its turn is going, here or on another device (and so while it works this screen). */
export function assistantLocked(): boolean {
  return busy || working;
}

/** assistantLocked, for components. */
export function useAssistantLocked(): boolean {
  return useSyncExternalStore(
    (l) => (workingListeners.add(l), () => void workingListeners.delete(l)),
    () => busy || working,
  );
}

/**
 * Whether 馍馍 is working this screen: from the first calls it runs here (a look alone doesn't count) until its turn ends. Meanwhile
 * the kit notes what the user does themselves (the agent's next answer has it), the chat gets out of the way and the top of the
 * window shows it working (ScreenWork), with a way to stop it.
 */
let working = false;
const workingListeners = new Set<() => void>();

/** While busy or working: now and then, whether the turn is still on (its end may have been missed: the chat and the cat stay locked till it is). */
let checkTimer: ReturnType<typeof setInterval> | null = null;

function recheck(): void {
  const on = busy || working;
  if (on === !!checkTimer) return;
  if (checkTimer) clearInterval(checkTimer);
  checkTimer = on
    ? setInterval(() => {
        void api()
          .assistant.state()
          .then((s) => {
            if (s.busy) return;
            setWorking(false);
            setBusy(false);
          })
          .catch(() => {});
      }, 20_000)
    : null;
}

function setWorking(on: boolean): void {
  if (on === working) return;
  working = on;
  kit.watchUser(on);
  recheck();
  for (const l of workingListeners) l();
}

/** Whether 馍馍 is working this screen now: the chat can't be opened or sent from (the server would refuse it too). */
export function screenWorking(): boolean {
  return working;
}

/** Whether 馍馍 is working this screen now (see working), for components. */
export function useScreenWorking(): boolean {
  return useSyncExternalStore(
    (l) => (workingListeners.add(l), () => void workingListeners.delete(l)),
    () => working,
  );
}

/** Stop 馍馍's turn (the 停止 by the progress bar): what it did stays done, its reply keeps what it had said. */
export async function stopScreenWork(): Promise<void> {
  await api().assistant.stop();
  setWorking(false);
}

function onEvent(event: MailEvent): void {
  if (event.type === 'assistant') {
    setBusy(event.busy);
    if (!event.busy) setWorking(false);
    // What it is on now (the step still running, else the tool it last named), for the pill at the top
    const last = event.steps?.at(-1);
    setDoing(event.busy ? (last && last.ms === undefined ? last.tool : event.step) : null);
  } else if (event.type === 'assistant-ui' && event.client === screenClient) {
    if ('calls' in (event.request as UiRequest)) setWorking(true);
    void handle(event.id, event.request as UiRequest);
  } else if (event.type === 'changed' && working) {
    // (back after the connection dropped: the end of the turn may have been missed meanwhile)
    void api()
      .assistant.state()
      .then((s) => !s.busy && setWorking(false))
      .catch(() => {});
  }
}

async function handle(id: string, request: UiRequest): Promise<void> {
  // A look: what was set off before it (a search, a mail opening) has loaded first
  if (!('calls' in request)) await settle();
  const answer = await kit.answer(request);
  // (the server gives up on its own after a while)
  await api()
    .assistant.uiAnswer(id, answer)
    .catch(() => {});
}

/**
 * The window was hidden (or came back) while 馍馍 works: the server stops waiting for this screen and finishes without it (the
 * phone does the same when it goes to the background).
 */
let reportedAway = false;
function onVisibility(): void {
  if (document.visibilityState === 'visible') {
    if (!reportedAway) return;
    reportedAway = false;
    void api()
      .assistant.presence(screenClient, true)
      .catch(() => {});
    return;
  }
  if (!busy) return;
  reportedAway = true;
  void api()
    .assistant.presence(screenClient, false)
    .catch(() => {});
}
